import * as Dialogo from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useDevolverFoco } from "./useDevolverFoco";

/*
 * O painel lateral de detalhe — doc 06 §4.2 e doc 06b §3.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O QUE SE COMPARA LINHA A LINHA ABRE AQUI, E NÃO EM PÁGINA NOVA.          │
 * │                                                                          │
 * │ "Operador compara linhas; tirar ele da lista quebra o fluxo de           │
 * │ trabalho." Ele abre um boleto, lê a linha do tempo, fecha, abre o de     │
 * │ baixo — e a lista precisa estar no mesmo lugar, com a mesma rolagem, a   │
 * │ cada vez.                                                                │
 * │                                                                          │
 * │ É por isso que o painel é uma camada sobre a lista, e não uma tela que a │
 * │ substitui. Critério, do design D1: tem abas e formulário, é página        │
 * │ (`PaginaDeEntidade`); é leitura com uma ou duas ações, é painel.         │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O painel pode ter URL, e a camada continua: ele é rota FILHA da lista, montado
 * pelo `Outlet` dela, e a lista segue montada embaixo, com a mesma rolagem. `aoFechar`
 * navega para o caminho da lista, com os filtros, e o voltar do navegador fecha o painel
 * do mesmo jeito. É assim em `/boletos/:id`, `/faturas/:id`, `/planos/:id` e
 * `/cedentes/:id/convenios/:convenioId`. Indeterminados abre o mesmo detalhe de boleto
 * por estado local, sobre a própria aba, porque navegar tiraria o operador de lá.
 *
 * Radix `Dialog` faz o trabalho invisível que ninguém lembra de escrever:
 * prende o foco dentro do painel, fecha com Esc, e marca o resto da página como
 * inerte para leitor de tela. Devolver o foco a quem abriu é com `useDevolverFoco`.
 */
export function PainelLateral({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  rodape,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Vai no cabeçalho E no rótulo acessível do diálogo. */
  titulo: ReactNode;
  /**
   * Descrição acessível. O Radix avisa no console se ela faltar — e o aviso
   * está certo: um diálogo sem descrição é anunciado só pelo título, e o
   * título aqui é uma referência de boleto.
   */
  descricao: string;
  children: ReactNode;
  /** Rodapé fixo de ações, sempre visível — não rola com o conteúdo. */
  rodape?: ReactNode;
}) {
  const devolverFoco = useDevolverFoco(aberto);

  return (
    <Dialogo.Root open={aberto} onOpenChange={(estado) => !estado && aoFechar()}>
      <Dialogo.Portal>
        {/*
          Sobreposição a 50%, como o doc 06a §5 pede. Ela escurece a lista sem
          escondê-la: o operador continua vendo onde estava.
        */}
        <Dialogo.Overlay className="sobreposicao fixed inset-0 z-40 bg-black/50" />

        <Dialogo.Content
          aria-describedby="descricao-do-painel"
          onCloseAutoFocus={devolverFoco}
          /*
           * A animação é de 180ms e vem do token — que `prefers-reduced-motion`
           * zera. Movimento não pedido sobre texto que alguém está lendo é o
           * que dispara enjoo em quem tem sensibilidade vestibular.
           */
          className="painel-lateral fixed inset-y-0 right-0 z-50 flex w-[var(--largura-painel-lateral)] max-w-[92vw] flex-col border-l border-fio bg-superficie"
        >
          <header className="flex items-start justify-between gap-3 border-b border-fio px-4 py-3">
            <Dialogo.Title asChild>
              <div className="min-w-0 flex-1">{titulo}</div>
            </Dialogo.Title>

            <Dialogo.Close
              aria-label="Fechar o detalhe"
              className="rounded-controle p-1 text-texto-2 hover:bg-superficie-2 hover:text-texto"
            >
              <X aria-hidden="true" className="size-4" />
            </Dialogo.Close>
          </header>

          <Dialogo.Description id="descricao-do-painel" className="sr-only">
            {descricao}
          </Dialogo.Description>

          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {rodape !== undefined && (
            <footer className="border-t border-fio bg-superficie px-4 py-3">{rodape}</footer>
          )}
        </Dialogo.Content>
      </Dialogo.Portal>
    </Dialogo.Root>
  );
}
